// Kişisel bilgi kartı: telefon, ev adresi, acil durum kişileri (E32, spec 18
// `feat_hris_self_service`).
//
// İKİ YÜZ, TEK KART. `/me/profile`da kişi KENDİ bilgisini düzenler
// (`/v1/me/personal-info`); `/people/:id`de İK yöneticisi başkasınınkini
// (`/v1/employees/:id/personal-info`). İki yüz aynı alanları aynı biçimde
// gösterip düzenlediği için tek bileşen, kaynak `source` ile seçilir: ayrı iki
// kopya bir alan eklendiği gün birbirinden ayrışırdı.
//
// Yalnız kişinin KENDİ görünümünde maaş IBAN'ı çıkar ve o SALT OKUNURDUR. Hesap
// bordronundur (spec 69); bu kart onu değiştirmek için bir yol açmaz, çünkü tek
// bir kimlik doğrulanmış oturumun maaşı başka bir hesaba yönlendirebilmesi bir
// ödeme sahtekârlığı yoludur. İK görünümü hesabı hiç görmez, uç onu göndermiyor.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";

import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  FieldRow,
  FormField,
  IconButton,
  Input,
  Loading,
} from "@novemai/components";

import { EnvelopeError, errorMessage } from "@/api/errors";

import {
  fetchEmployeePersonalInfo,
  fetchMyPersonalInfo,
  MAX_EMERGENCY_CONTACTS,
  PERSONAL_INFO_KEY,
  updateEmployeePersonalInfo,
  updateMyPersonalInfo,
  type Address,
  type PersonalInfo,
  type PersonalInfoPatch,
  type SelfPersonalInfo,
} from "./personal-info-api";
import {
  buildPatch,
  draftFromInfo,
  emptyContact,
  type ContactDraft,
  type PersonalInfoDraft,
} from "./personal-info-form";

export type PersonalInfoSource =
  | { readonly kind: "self" }
  | { readonly kind: "hr"; readonly employeeId: string };

// Kaydı okuyamayan (403) ya da artık var olmayan (404, silinmiş kişi) İK
// görünümünde kart hiç çizilmez: bu bir hata değil, "sana gösterilecek bir şey
// yok". Kişinin kendi görünümünde ise ikisi de gerçek bir arızadır.
function isAbsentForCaller(error: unknown): boolean {
  return (
    error instanceof EnvelopeError && (error.httpStatus === 403 || error.httpStatus === 404)
  );
}

function countryName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

function addressLines(a: Address, locale: string): readonly string[] {
  return [
    [a.line1, a.line2].filter((v): v is string => v !== null && v !== "").join(", "),
    [a.postal_code, a.city, a.state_province].filter((v): v is string => v !== null && v !== "").join(" "),
    countryName(a.country, locale),
  ];
}

export function PersonalInfoCard({
  source,
  absent = null,
}: {
  readonly source: PersonalInfoSource;
  /** Drawn in place of the card when the HR read is refused or the person is gone. */
  readonly absent?: React.ReactElement | null;
}): React.ReactElement | null {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);

  const queryKey = [
    ...PERSONAL_INFO_KEY,
    source.kind === "self" ? "me" : source.employeeId,
  ] as const;
  const query = useQuery<PersonalInfo | SelfPersonalInfo>({
    queryKey,
    queryFn: () =>
      source.kind === "self" ? fetchMyPersonalInfo() : fetchEmployeePersonalInfo(source.employeeId),
  });

  const save = useMutation({
    mutationFn: (patch: PersonalInfoPatch) =>
      source.kind === "self"
        ? updateMyPersonalInfo(patch)
        : updateEmployeePersonalInfo(source.employeeId, patch),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKey, next);
      setEditing(false);
      setSaved(true);
    },
  });

  if (source.kind === "hr" && query.isError && isAbsentForCaller(query.error)) return absent;

  const isSelf = source.kind === "self";
  const none = t("people.personal.none");
  const info = query.data;

  return (
    <Card data-tour={isSelf ? "profile-personal-info" : undefined}>
      <CardHeader>
        <div>
          <div className="nai-card-title">{t("people.personal.title")}</div>
          <p className="mt-0.5 text-[12px] text-[var(--fg-muted)]">
            {isSelf ? t("people.personal.lead_self") : t("people.personal.lead_hr")}
          </p>
        </div>
        {info !== undefined && !editing && (
          <Button
            variant="secondary"
            onClick={(): void => {
              setSaved(false);
              save.reset();
              setEditing(true);
            }}
          >
            {t("people.personal.edit")}
          </Button>
        )}
      </CardHeader>
      <CardBody>
        {query.isLoading ? (
          <Loading label={t("people.personal.loading")} rows={2} />
        ) : query.isError || info === undefined ? (
          <ErrorState
            title={t("people.personal.error_title")}
            description={errorMessage(query.error, t)}
          />
        ) : editing ? (
          <PersonalInfoForm
            info={info}
            saving={save.isPending}
            saveError={save.isError ? errorMessage(save.error, t) : null}
            onCancel={(): void => {
              save.reset();
              setEditing(false);
            }}
            onSubmit={(patch): void => {
              // Değişen bir şey yoksa uç boş gövdeyi 400 ile reddederdi.
              if (Object.keys(patch).length === 0) {
                setEditing(false);
                return;
              }
              save.mutate(patch);
            }}
          />
        ) : (
          <>
            {saved && (
              <p role="status" className="mb-3 text-[12.5px] text-[var(--state-success)]">
                {t("people.personal.saved")}
              </p>
            )}
            <FieldRow label={t("people.personal.field_phone")} value={info.primary_phone ?? none} />
            <FieldRow
              label={t("people.personal.field_address")}
              value={
                info.home_address === null ? (
                  none
                ) : (
                  <span className="block">
                    {addressLines(info.home_address, i18n.language).map((line) => (
                      <span key={line} className="block">
                        {line}
                      </span>
                    ))}
                  </span>
                )
              }
            />
            <FieldRow
              label={t("people.personal.field_contacts")}
              value={
                info.emergency_contacts.length === 0 ? (
                  t("people.personal.contacts_none")
                ) : (
                  <ul className="m-0 list-none space-y-2 p-0">
                    {info.emergency_contacts.map((c) => (
                      <li key={`${c.name}|${c.phone}`}>
                        <span className="block">
                          {t("people.personal.contact_line", {
                            name: c.name,
                            relationship: c.relationship,
                          })}
                        </span>
                        <span className="block text-[12px] text-[var(--fg-muted)]">
                          {[c.phone, c.email].filter((v): v is string => v !== null).join(" · ")}
                        </span>
                      </li>
                    ))}
                  </ul>
                )
              }
            />
            {"payroll_bank_account" in info && (
              <>
                <FieldRow
                  label={t("people.personal.field_iban")}
                  value={
                    info.payroll_bank_account === null
                      ? t("people.personal.iban_none")
                      : [info.payroll_bank_account.bank_name, info.payroll_bank_account.iban_masked]
                          .filter((v): v is string => v !== null)
                          .join(" · ")
                  }
                  mono={info.payroll_bank_account !== null}
                />
                <p className="mt-2 text-[12px] text-[var(--fg-muted)]">
                  {t("people.personal.iban_note")}
                </p>
              </>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}

function PersonalInfoForm({
  info,
  saving,
  saveError,
  onCancel,
  onSubmit,
}: {
  readonly info: PersonalInfo;
  readonly saving: boolean;
  readonly saveError: string | null;
  readonly onCancel: () => void;
  readonly onSubmit: (patch: PersonalInfoPatch) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<PersonalInfoDraft>(() => draftFromInfo(info));
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});

  const setAddress = (field: keyof PersonalInfoDraft["address"], value: string): void => {
    setDraft((d) => ({ ...d, address: { ...d.address, [field]: value } }));
  };
  const setContact = (index: number, field: keyof ContactDraft, value: string): void => {
    setDraft((d) => ({
      ...d,
      contacts: d.contacts.map((c, i) => (i === index ? { ...c, [field]: value } : c)),
    }));
  };

  return (
    <form
      noValidate
      className="space-y-5"
      onSubmit={(event): void => {
        event.preventDefault();
        const result = buildPatch(draft, info, t);
        if (!result.ok) {
          setErrors(result.errors);
          return;
        }
        setErrors({});
        onSubmit(result.patch);
      }}
    >
      {saveError !== null && (
        <Alert severity="danger" title={t("people.personal.save_error_title")}>
          {saveError}
        </Alert>
      )}

      <FormField
        label={t("people.personal.field_phone")}
        hint={t("people.personal.phone_hint")}
        error={errors["phone"]}
      >
        <Input
          type="tel"
          autoComplete="tel"
          value={draft.phone}
          onChange={(e): void => setDraft((d) => ({ ...d, phone: e.target.value }))}
        />
      </FormField>

      <fieldset className="space-y-3 border-0 p-0">
        <legend className="mb-1 text-[13px] font-medium text-[var(--fg-primary)]">
          {t("people.personal.field_address")}
        </legend>
        <p className="text-[12px] text-[var(--fg-muted)]">{t("people.personal.address_clear_hint")}</p>
        <FormField label={t("people.personal.address_line_one")} error={errors["address.line1"]}>
          <Input
            autoComplete="address-line1"
            value={draft.address.line1}
            onChange={(e): void => setAddress("line1", e.target.value)}
          />
        </FormField>
        <FormField label={t("people.personal.address_line_two")}>
          <Input
            autoComplete="address-line2"
            value={draft.address.line2}
            onChange={(e): void => setAddress("line2", e.target.value)}
          />
        </FormField>
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label={t("people.personal.address_city")} error={errors["address.city"]}>
            <Input
              autoComplete="address-level2"
              value={draft.address.city}
              onChange={(e): void => setAddress("city", e.target.value)}
            />
          </FormField>
          <FormField label={t("people.personal.address_state")}>
            <Input
              autoComplete="address-level1"
              value={draft.address.state_province}
              onChange={(e): void => setAddress("state_province", e.target.value)}
            />
          </FormField>
          <FormField label={t("people.personal.address_postal_code")}>
            <Input
              autoComplete="postal-code"
              value={draft.address.postal_code}
              onChange={(e): void => setAddress("postal_code", e.target.value)}
            />
          </FormField>
          <FormField
            label={t("people.personal.address_country")}
            hint={t("people.personal.address_country_hint")}
            error={errors["address.country"]}
          >
            <Input
              autoComplete="country"
              maxLength={2}
              value={draft.address.country}
              onChange={(e): void => setAddress("country", e.target.value)}
            />
          </FormField>
        </div>
      </fieldset>

      <fieldset className="space-y-3 border-0 p-0">
        <legend className="mb-1 text-[13px] font-medium text-[var(--fg-primary)]">
          {t("people.personal.field_contacts")}
        </legend>
        {draft.contacts.map((contact, index) => (
          // Konum anahtarı bilerek: satırın kimliği yok, silme yalnız konum
          // kaydırır ve satırların değeri kontrollü durumda tutuluyor.
          <div key={index} className="space-y-3 rounded-lg border border-[var(--border-subtle)] p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[12.5px] font-medium text-[var(--fg-secondary)]">
                {t("people.personal.contact_heading", { position: index + 1 })}
              </span>
              <IconButton
                aria-label={t("people.personal.contact_remove", { position: index + 1 })}
                onClick={(): void =>
                  setDraft((d) => ({ ...d, contacts: d.contacts.filter((_, i) => i !== index) }))
                }
              >
                <Trash2 size={14} aria-hidden="true" />
              </IconButton>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                label={t("people.personal.contact_name")}
                error={errors[`contacts.${index}.name`]}
              >
                <Input
                  value={contact.name}
                  onChange={(e): void => setContact(index, "name", e.target.value)}
                />
              </FormField>
              <FormField
                label={t("people.personal.contact_relationship")}
                error={errors[`contacts.${index}.relationship`]}
              >
                <Input
                  value={contact.relationship}
                  onChange={(e): void => setContact(index, "relationship", e.target.value)}
                />
              </FormField>
              <FormField
                label={t("people.personal.contact_phone")}
                error={errors[`contacts.${index}.phone`]}
              >
                <Input
                  type="tel"
                  value={contact.phone}
                  onChange={(e): void => setContact(index, "phone", e.target.value)}
                />
              </FormField>
              <FormField
                label={t("people.personal.contact_email")}
                error={errors[`contacts.${index}.email`]}
              >
                <Input
                  type="email"
                  value={contact.email}
                  onChange={(e): void => setContact(index, "email", e.target.value)}
                />
              </FormField>
            </div>
          </div>
        ))}
        {errors["contacts"] !== undefined && (
          <p role="alert" className="text-[12.5px] text-[var(--color-error)]">
            {errors["contacts"]}
          </p>
        )}
        {draft.contacts.length < MAX_EMERGENCY_CONTACTS ? (
          <Button
            type="button"
            variant="secondary"
            onClick={(): void =>
              setDraft((d) => ({ ...d, contacts: [...d.contacts, emptyContact()] }))
            }
          >
            <Plus size={14} aria-hidden="true" />
            {t("people.personal.contact_add")}
          </Button>
        ) : (
          <p className="text-[12px] text-[var(--fg-muted)]">
            {t("people.personal.contacts_max", { count: MAX_EMERGENCY_CONTACTS })}
          </p>
        )}
      </fieldset>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" variant="primary" disabled={saving}>
          {t("people.personal.save")}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
          {t("common.cancel")}
        </Button>
      </div>
    </form>
  );
}
